import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import type {MenuItemProps} from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import {useId} from "react";
import {useTranslation} from "react-i18next";
import {useServerTableContext} from "../ServerTableContext";
import {
    useServerTableToolbarContext,
    useServerTableToolbarItemContext
} from "../ServerTableToolbarContext";
import {defineServerTableToolbarItem} from "../ServerTableToolbarItem";
import {serverTableControlIconSx, serverTableControlSx} from "../constants";
import ColumnConfiguratorPanel from "./ColumnConfiguratorPanel";

const ServerTableColumnsComponent = (
    menuItemProps: Omit<Partial<MenuItemProps>, "id"> = {},
) => {
    const {t} = useTranslation();
    const {table} = useServerTableContext();
    const popoverId = useId();
    const {activePanel, openPanel, closePanel} = useServerTableToolbarContext();
    const toolbarItem = useServerTableToolbarItemContext();

    const isOpen = activePanel?.type === 'columns';
    const openColumns = (anchor: HTMLElement) => {
        toolbarItem.closeMenu();
        openPanel('columns', toolbarItem.menuAnchor ?? anchor);
    };

    return (
        <>
            {toolbarItem.presentation === 'menu' ? (
                <MenuItem
                    {...menuItemProps}
                    onClick={event => openColumns(event.currentTarget)}
                >
                    <ViewColumnOutlinedIcon fontSize="small" sx={{mr: 1, color: 'primary.main'}}/>
                    {t('Columns')}
                </MenuItem>
            ) : (
                <Tooltip title={t('Columns')}>
                    <IconButton
                        size="small"
                        color="primary"
                        data-dashboard-control=""
                        data-active={isOpen || undefined}
                        aria-label={t('Columns')}
                        aria-haspopup="dialog"
                        aria-expanded={isOpen}
                        aria-controls={isOpen ? popoverId : undefined}
                        onClick={event => openColumns(event.currentTarget)}
                        sx={serverTableControlSx}
                    >
                        <ViewColumnOutlinedIcon sx={serverTableControlIconSx}/>
                    </IconButton>
                </Tooltip>
            )}

            <Popover
                open={isOpen}
                anchorEl={isOpen ? activePanel.anchor : null}
                onClose={closePanel}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                PaperProps={{
                    id: popoverId,
                    role: 'dialog',
                    'aria-label': t('Columns'),
                    sx: {overflow: 'hidden'}
                }}
            >
                <ColumnConfiguratorPanel table={table} onClose={closePanel}/>
            </Popover>
        </>
    );
};

export const ServerTableColumns = defineServerTableToolbarItem(ServerTableColumnsComponent);
